import React, { useEffect, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';
import { OfferFacts, offerFactLines } from '../components/ChatWidget/offerFactsText';

// ============================================================
// The InvestMenter Brain on the public app tour — for visitors without an
// account. It explains the app first, then advises on Dubai property, shows
// Dubai Sell units that fit, and takes a lead only once the visitor agrees to
// a call. POST /api/brain/guest; the visitor's profile is kept here, in the
// page, and sent back each turn (the server stores nothing but a lead).
// ============================================================

// Same origin rule as shared/sellListings.ts: strip /api before re-adding it.
const API_ORIGIN = (
  process.env.REACT_APP_API_URL || 'https://investmenter-backend-1081814658115.europe-west1.run.app'
).replace(/\/api\/?$/, '');

interface Offer {
  id: string;
  title: string | null;
  propertyType?: string | null;
  location: string | null;
  sizeSqft: number | null;
  bedrooms: number | null;
  askingPrice: number | null;
  isDistress?: boolean;
  discountPercent?: number | null;
  photoUrl: string | null;
  facts?: OfferFacts;
}

interface Msg {
  id: string;
  text: string;
  isUser: boolean;
  offers?: Offer[];
}

/** "1. Option" lines at the end of a reply become tap answers. */
function splitChoices(text: string): { body: string; choices: string[] } {
  const lines = text.split('\n');
  const choices: string[] = [];
  while (lines.length && /^\s*$/.test(lines[lines.length - 1])) lines.pop();
  while (lines.length) {
    const m = lines[lines.length - 1].match(/^\s*[1-9][.)]\s+(.+)$/);
    if (!m) break;
    choices.unshift(m[1].trim());
    lines.pop();
  }
  return choices.length >= 2 ? { body: lines.join('\n').trim(), choices } : { body: text, choices: [] };
}

const TourBrainChat: React.FC = () => {
  const { t, language } = useLanguage();
  const de = language === 'de';
  const [messages, setMessages] = useState<Msg[]>([]);
  const [profile, setProfile] = useState<Record<string, string>>({});
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // The greeting is local: instant, and costs nothing until the visitor answers.
  // It follows the page language until the conversation has started.
  const started = messages.some(m => m.isUser);
  useEffect(() => {
    if (!started) setMessages([{ id: 'greeting', text: t('tourAiGreeting'), isUser: false }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const send = async (text: string) => {
    const message = text.trim();
    if (!message || sending) return;
    setError(null);
    const history = messages.map(m => ({ text: m.text, isUser: m.isUser }));
    setMessages(prev => [...prev, { id: `u${Date.now()}`, text: message, isUser: true }]);
    setInput('');
    setSending(true);
    try {
      const r = await fetch(`${API_ORIGIN}/api/brain/guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, history, language: de ? 'de' : 'en', profile }),
      });
      const j = await r.json().catch(() => null);
      if (r.status === 429) { setError(t('tourAiLimit')); return; }
      if (!r.ok || !j?.success) { setError(t('tourAiError')); return; }
      if (j.profile && typeof j.profile === 'object') setProfile(j.profile);
      setMessages(prev => [...prev, {
        id: `a${Date.now()}`, text: j.message, isUser: false,
        ...(Array.isArray(j.offers) && j.offers.length ? { offers: j.offers } : {}),
      }]);
    } catch {
      setError(t('tourAiError'));
    } finally {
      setSending(false);
    }
  };

  const lastAgent = [...messages].reverse().find(m => !m.isUser);
  const fmt = (n: number) => Math.round(n).toLocaleString(de ? 'de-DE' : 'en-AE');

  return (
    <div className="tour-ai">
      <div className="tour-ai-list" ref={listRef} aria-live="polite">
        {messages.map(m => {
          const { body, choices } = m.isUser ? { body: m.text, choices: [] as string[] } : splitChoices(m.text);
          const showChoices = !m.isUser && m === lastAgent && !sending && choices.length > 0;
          return (
            <div key={m.id} className={`tour-ai-row ${m.isUser ? 'is-user' : ''}`}>
              <div className="tour-ai-bubble">{body}</div>
              {m.offers && m.offers.length > 0 && (
                <div className="tour-ai-offers">
                  {m.offers.map(o => {
                    const name = o.title || o.propertyType || (de ? 'Einheit' : 'Unit');
                    const meta = [
                      o.location,
                      o.bedrooms != null ? (o.bedrooms === 0 ? 'Studio' : `${o.bedrooms} BR`) : null,
                      o.sizeSqft ? `${fmt(o.sizeSqft)} sqft` : null,
                    ].filter(Boolean).join(' · ');
                    return (
                      <div key={o.id} className="tour-ai-offer">
                        {o.photoUrl && <img src={o.photoUrl} alt="" loading="lazy" />}
                        <div className="tour-ai-offer-body">
                          <div className="tour-ai-offer-name">{name}</div>
                          {meta && <div className="tour-ai-offer-meta">{meta}</div>}
                          {o.askingPrice ? (
                            <div className="tour-ai-offer-price">
                              {de ? `${fmt(o.askingPrice)} AED` : `AED ${fmt(o.askingPrice)}`}
                              {o.isDistress && o.discountPercent ? <span>−{o.discountPercent}%</span> : null}
                            </div>
                          ) : null}
                          {offerFactLines(o.facts, de).map((fl) => (
                            <div key={fl.text} className={`tour-ai-offer-fact ${fl.good ? 'is-good' : ''}`}>{fl.text}</div>
                          ))}
                          {m === lastAgent && (
                            <button type="button" disabled={sending}
                              onClick={() => send(de ? `Ich interessiere mich für ${name}.` : `I am interested in ${name}.`)}>
                              {t('tourAiInterested')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {showChoices && (
                <div className="tour-ai-choices">
                  {choices.map(c => (
                    <button key={c} type="button" onClick={() => send(c)}>{c}</button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {sending && <div className="tour-ai-row"><div className="tour-ai-bubble tour-ai-typing">{t('tourAiThinking')}</div></div>}
        {error && <div className="tour-ai-error">{error}</div>}
      </div>

      <form className="tour-ai-input" onSubmit={(e) => { e.preventDefault(); send(input); }}>
        <input
          type="text"
          value={input}
          maxLength={1000}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('tourAiPlaceholder')}
          aria-label={t('tourAiPlaceholder')}
        />
        <button type="submit" disabled={sending || !input.trim()} aria-label={t('tourAiSend')}>
          <ArrowUp size={18} />
        </button>
      </form>
      <p className="tour-ai-note">{t('tourAiNote')}</p>
    </div>
  );
};

export default TourBrainChat;
